// Loss animation orchestrator.
//
// Plays once on a live loss, after the flick that exhausts the player's
// undos lands and before the endgame modal opens. The board comes apart:
// every lettered tile and ~60% of randomly-sampled empty cells fall under
// gravity off the bottom of the screen with a small upward "knocked loose"
// hop, a tumble, and a sideways drift. No stagger — everything starts at
// the same instant; randomness in the drift / rotation / sampling does the
// visual heavy lifting.
//
// The single bar tile that most recently animated into the bar (the
// rightmost filled slot in `state.exitedLetters` at loss time) pulses
// scale 1.08 twice (620ms × 2) alongside the fall. Other bar tiles stay
// still. No color change anywhere.
//
// Reduced-motion users get a 150ms fade in place of the fall+tumble
// (driven by the same data-attribute hooks; CSS overrides the keyframes
// under prefers-reduced-motion). The Promise resolves on the same
// schedule so the modal pacing stays predictable.
//
// Mirrors victoryAnimation.ts structurally: single setTimeout for promise
// resolution (robust to mid-animation DOM mutation), defensive empty-set
// early return, central reduced-motion gate.

import { shouldReduceMotion } from './animator'
import type { Puzzle } from './puzzle'
import type { GameState } from './state'

// Probability a given empty (unlettered) cell joins the fall. Lettered
// tiles always fall. Tuned so the board feels like it disintegrates
// without the empty cells dominating the scene.
const P_EMPTY_CELL_JOINS = 0.6

// Total fall duration. Promise resolves at this exact mark so the modal
// opens the instant the last piece has cleared the viewport.
const FALL_DURATION_MS = 1200

// Reduced-motion fade-out duration. Matches the CSS transition under the
// prefers-reduced-motion media query.
const REDUCED_MOTION_FADE_MS = 150

// Horizontal drift bounds, as a fraction of the board's width. Each piece
// gets a uniform draw inside [MIN, MIN + RANGE].
const DRIFT_MIN_FRACTION = 0.04
const DRIFT_RANGE_FRACTION = 0.24

// Rotation magnitude bounds, in degrees. Each piece gets a uniform draw
// inside [MIN, MIN + RANGE], then a random sign.
const ROT_MIN_DEG = 60
const ROT_RANGE_DEG = 260

// Vertical fall distance as a multiple of the board's height. 1.8× clears
// the viewport for boards positioned near the top of a tall layout.
const FALL_DISTANCE_FACTOR = 1.8

// Find the rightmost slot in the bar that has a letter committed to it.
// Returns null if the bar is empty (defensive — a loss after zero flicks
// is technically possible mid-development but should never happen in the
// shipping game; we degrade gracefully either way).
function findLastBarSlotIndex(state: GameState): number | null {
  for (let i = state.exitedLetters.length - 1; i >= 0; i -= 1) {
    const letter = state.exitedLetters[i]
    if (letter !== undefined) return i
  }
  return null
}

// Mark the rightmost filled bar slot with the pulse attribute. Other
// slots stay untouched. No-op if the bar element doesn't have a child at
// the targeted index (defensive: render skew between state + DOM).
function markBarPulse(barEl: HTMLElement, state: GameState): void {
  const idx = findLastBarSlotIndex(state)
  if (idx === null) return
  const slot = barEl.children[idx]
  if (!(slot instanceof HTMLElement)) return
  slot.setAttribute('data-loss-bar-pulse', 'true')
}

// A running loss animation. `done` resolves when the fall finishes (or is
// cancelled). `cancel()` stops it immediately — clears the fall timer, tears
// down the board/bar/document state (the SAME teardown restoreBoardAfterLoss
// does, including releasing the global `loss-animating` body lock), and
// resolves `done`. So a player who navigates mid-fall gets an instant clean
// swap with no stuck overflow lock and no falling tiles painting on the next
// puzzle. (Without this, navigating during the fall would skip the post-modal
// restoreBoardAfterLoss and strand the body lock — see main.ts's generation
// guard, which bails before that restore.)
export interface LossAnimationHandle {
  readonly done: Promise<void>
  cancel(): void
}

export function playLossAnimation(
  boardEl: HTMLElement,
  barEl: HTMLElement,
  _puzzle: Puzzle,
  state: GameState,
): LossAnimationHandle {
  const timers: ReturnType<typeof setTimeout>[] = []
  let settled = false
  let resolveDone: () => void = (): void => {}
  const done = new Promise<void>((resolve): void => {
    resolveDone = resolve
  })
  const schedule = (fn: () => void, ms: number): void => {
    timers.push(setTimeout(fn, ms))
  }
  const finish = (): void => {
    if (settled) return
    settled = true
    resolveDone()
  }
  const cancel = (): void => {
    if (settled) return
    settled = true
    for (const t of timers) clearTimeout(t)
    // Full teardown so a mid-fall navigation can't strand the falling tiles or
    // the global overflow lock. Idempotent with the normal post-modal restore.
    restoreBoardAfterLoss(boardEl, barEl)
    resolveDone()
  }

  // Reduced-motion path: mark every lettered tile + every empty cell with
  // the falling attribute (CSS converts to a fade under prefers-reduced-motion;
  // no 60% sampling needed — fade is non-disruptive). Bar pulse is also
  // suppressed by the reduced-motion CSS override.
  if (shouldReduceMotion()) {
    const filled = boardEl.querySelectorAll<HTMLElement>(
      '.cell[data-filled="true"]',
    )
    const empty = boardEl.querySelectorAll<HTMLElement>(
      '.cell:not([data-filled="true"])',
    )
    for (const el of filled) el.classList.add('tile--falling')
    for (const el of empty) el.classList.add('cell--falling')
    markBarPulse(barEl, state)
    schedule(finish, REDUCED_MOTION_FADE_MS)
    return { done, cancel }
  }

  const filledCells = Array.from(
    boardEl.querySelectorAll<HTMLElement>('.cell[data-filled="true"]'),
  )
  const emptyCells = Array.from(
    boardEl.querySelectorAll<HTMLElement>('.cell:not([data-filled="true"])'),
  )

  // Sample empty cells at P_EMPTY_CELL_JOINS. Lettered tiles always fall.
  const sampledEmpty: HTMLElement[] = []
  for (const cell of emptyCells) {
    if (Math.random() < P_EMPTY_CELL_JOINS) sampledEmpty.push(cell)
  }

  const lastBarIdx = findLastBarSlotIndex(state)

  // Defensive guard: nothing to animate and no bar pulse — resolve
  // immediately so the modal still opens. Filled cells + sampled empty
  // cells + bar pulse all empty means there's no visible loss animation
  // to wait for.
  if (
    filledCells.length === 0 &&
    sampledEmpty.length === 0 &&
    lastBarIdx === null
  ) {
    finish()
    return { done, cancel }
  }

  // Let falling pieces escape the board's bounding box visually. The
  // board container would otherwise clip them at its bottom edge.
  boardEl.dataset['lossOverflow'] = 'true'

  // Lock document overflow for the duration of the fall. Transformed
  // pieces extend scrollable overflow per CSS Transforms L1 — without
  // this lock, iOS Safari minimizes its URL bar (recomputing dvh) and
  // zooms the page out to fit the horizontally-overflowing content.
  // Either symptom looks like the whole app shrinking just before the
  // modal opens. Released by restoreBoardAfterLoss (or cancel()).
  document.body.classList.add('loss-animating')

  const rect = boardEl.getBoundingClientRect()
  const widthPx = rect.width
  const heightPx = rect.height
  const fallPx = heightPx * FALL_DISTANCE_FACTOR

  const applyFallStyle = (el: HTMLElement): void => {
    const signX = Math.random() < 0.5 ? -1 : 1
    const signR = Math.random() < 0.5 ? -1 : 1
    const drift = DRIFT_MIN_FRACTION + Math.random() * DRIFT_RANGE_FRACTION
    const rotMag = ROT_MIN_DEG + Math.random() * ROT_RANGE_DEG
    el.style.setProperty('--fx', `${String(signX * drift * widthPx)}px`)
    el.style.setProperty('--fall', `${String(fallPx)}px`)
    el.style.setProperty('--rot', `${String(signR * rotMag)}deg`)
  }

  for (const tile of filledCells) {
    applyFallStyle(tile)
    tile.classList.add('tile--falling')
  }
  for (const cell of sampledEmpty) {
    applyFallStyle(cell)
    cell.classList.add('cell--falling')
  }

  markBarPulse(barEl, state)

  schedule(finish, FALL_DURATION_MS)
  return { done, cancel }
}

// Snap the board + bar back to the post-loss DOM state (the same DOM the
// completeRender produced before the animation overlay was applied). Called
// behind the open endgame modal so the player sees their final board when
// they close the modal — not the disintegrated state the animation left.
export function restoreBoardAfterLoss(
  boardEl: HTMLElement,
  barEl: HTMLElement,
): void {
  const falling = boardEl.querySelectorAll<HTMLElement>(
    '.tile--falling, .cell--falling',
  )
  for (const el of falling) {
    el.classList.remove('tile--falling', 'cell--falling')
    el.style.removeProperty('--fx')
    el.style.removeProperty('--fall')
    el.style.removeProperty('--rot')
  }
  delete boardEl.dataset['lossOverflow']
  document.body.classList.remove('loss-animating')
  const pulsed = barEl.querySelector<HTMLElement>('[data-loss-bar-pulse]')
  if (pulsed !== null) pulsed.removeAttribute('data-loss-bar-pulse')
}
